/**
 * TravelReport — the "12 months of travel" card.
 *
 * Renders trips (from the onboarding calendar scan) as a chief-of-staff
 * travel report: summary strip (trips / nights away / destinations /
 * countries), a "most visited" line, and a year-grouped timeline where
 * adjacent legs collapse into journeys ("Paris → chateau → Paris" is one
 * Aug trip), hotels run muted under the title, and an accent now-line
 * divides past from planned. Used by the onboarding reveal AND the /travel
 * command's result card.
 */

export interface TripRow {
  destination: string;
  dateRange: string;
  city?: string;
  lodging?: string;
  startISO?: string;
  endISO?: string;
  planned?: boolean;
}

// Adjacent trips (gap ≤ 36h) merge into one JOURNEY.
interface Journey {
  stops: string[];
  hotels: string[];
  start: Date;
  end: Date;
  planned: boolean;
}

const FRENCH_TOKENS = new Set(['PARIS', 'CHAMONIX', 'CHATEAU', 'BIARRITZ', 'NICE', 'FRANCE', 'CDG', 'PROVENCE', 'LYON', 'CANNES']);
const isFrench = (place: string) => placeWords(place).some((w) => FRENCH_TOKENS.has(w));
const placeWords = (s: string) => (s.toUpperCase().match(/[A-ZÀ-Ö]{3,}/g) ?? []);

function buildJourneys(trips: TripRow[]): Journey[] {
  const dated = trips
    .filter((t) => t.startISO)
    .map((t) => ({
      ...t,
      s: new Date(t.startISO!),
      e: new Date(t.endISO ?? t.startISO!),
    }))
    .sort((a, b) => a.s.getTime() - b.s.getTime());
  const journeys: Journey[] = [];
  for (const t of dated) {
    const place = t.city ?? t.destination;
    const last = journeys[journeys.length - 1];
    if (last && !t.planned === !last.planned && t.s.getTime() - last.end.getTime() <= 36 * 60 * 60 * 1000) {
      if (last.stops[last.stops.length - 1] !== place) last.stops.push(place);
      if (t.lodging) last.hotels.push(t.lodging);
      if (t.e > last.end) last.end = t.e;
      continue;
    }
    journeys.push({ stops: [place], hotels: t.lodging ? [t.lodging] : [], start: t.s, end: t.e, planned: !!t.planned });
  }
  return journeys;
}

const nightsOf = (j: Journey) => Math.max(1, Math.round((j.end.getTime() - j.start.getTime()) / 86_400_000));

function journeyTitle(j: Journey): { title: string; route?: string } {
  if (j.stops.every(isFrench)) {
    return { title: 'France', route: j.stops.length > 1 ? j.stops.join(' → ') : j.stops[0] };
  }
  if (j.stops.length === 1) return { title: j.stops[0] };
  return { title: j.stops.join(' → ') };
}

function journeyDates(j: Journey): string {
  const mShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short' });
  const s = j.start;
  const e = j.end;
  if (e.toDateString() === s.toDateString()) return `${mShort(s)} ${s.getDate()}`;
  return s.getMonth() === e.getMonth()
    ? `${mShort(s)} ${s.getDate()}–${e.getDate()}`
    : `${mShort(s)} ${s.getDate()} – ${mShort(e)} ${e.getDate()}`;
}

export function TravelReport({ trips }: { trips: TripRow[] }) {
  const journeys = buildJourneys(trips);
  const past = journeys.filter((j) => !j.planned);
  const planned = journeys.filter((j) => j.planned);
  const allStops = journeys.flatMap((j) => j.stops);
  const destinations = new Set(allStops.map((s) => s.toLowerCase()));
  const countries = new Set(allStops.map((s) => (isFrench(s) ? 'France' : 'United States')));
  const nights = past.reduce((n, j) => n + nightsOf(j), 0);
  const visitCounts = new Map<string, number>();
  for (const s of allStops) visitCounts.set(s, (visitCounts.get(s) ?? 0) + 1);
  const mostVisited = [...visitCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
  const now = new Date();
  const nowLabel = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();

  const renderJourney = (j: Journey, key: string) => {
    const { title, route } = journeyTitle(j);
    return (
      <div className="otr-row" key={key}>
        <div className="otr-when">
          <div className="otr-dates">{journeyDates(j)}</div>
          <div className="otr-nights">{nightsOf(j)} night{nightsOf(j) === 1 ? '' : 's'}</div>
        </div>
        <div className={`otr-dot${j.planned ? ' otr-dot--planned' : ''}`} />
        <div className="otr-body">
          <div className="otr-title">
            {title}
            {route && <span className="otr-route"> · {route}</span>}
            {j.planned && <span className="oal-trips-tag">planned</span>}
          </div>
          {j.hotels.length > 0 && <div className="otr-hotels">{j.hotels.join(' · ')}</div>}
        </div>
      </div>
    );
  };

  let lastYear = 0;
  return (
    <div className="otr">
      <div className="otr-label">Where you've been</div>
      <div className="otr-heading">12 months of travel</div>
      <div className="otr-stats">
        {[[past.length, past.length === 1 ? 'trip' : 'trips'], [nights, 'nights away'], [destinations.size, 'destinations'], [countries.size, countries.size === 1 ? 'country' : 'countries']].map(([n, label], i) => (
          <div className="otr-stat" key={i}>
            <div className="otr-stat-n">{n}</div>
            <div className="otr-stat-label">{label}</div>
          </div>
        ))}
      </div>
      {mostVisited.length > 0 && (
        <div className="otr-most">
          Most visited{'  '}
          {mostVisited.map(([city, n], i) => (
            <span key={city} className="otr-most-city">
              {i > 0 && ' · '}
              <strong>{city}</strong>
              {n > 1 && <span className="otr-most-n"> ×{n}</span>}
            </span>
          ))}
        </div>
      )}
      {past.map((j, i) => {
        const year = j.start.getFullYear();
        const header = year !== lastYear ? <div className="otr-year" key={`y${year}`}>{year}</div> : null;
        lastYear = year;
        return (
          <span key={i}>
            {header}
            {renderJourney(j, `p${i}`)}
          </span>
        );
      })}
      {planned.length > 0 && (
        <>
          <div className="otr-now">NOW · {nowLabel}</div>
          {planned.map((j, i) => renderJourney(j, `f${i}`))}
        </>
      )}
    </div>
  );
}

// "Jul 8 – 13, 2025" / "Aug 31 – Sep 4, 2025" from real trip dates; the
// yearless dateRange string is the fallback.
function fmtTripDates(t: TripRow): string {
  if (!t.startISO) return t.dateRange;
  const s = new Date(t.startISO);
  if (Number.isNaN(s.getTime())) return t.dateRange;
  const e = t.endISO ? new Date(t.endISO) : null;
  const mShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short' });
  let range = `${mShort(s)} ${s.getDate()}`;
  if (e && !Number.isNaN(e.getTime()) && e.toDateString() !== s.toDateString()) {
    range += s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
      ? ` – ${e.getDate()}`
      : ` – ${mShort(e)} ${e.getDate()}`;
  }
  return `${range}, ${(e && !Number.isNaN(e.getTime()) ? e : s).getFullYear()}`;
}

export function TripsTable({ trips }: { trips: TripRow[] }) {
  return (
    <table className="oal-trips">
      <tbody>
        {trips.map((t, i) => (
          <tr key={i} className={t.planned ? 'oal-trips-row oal-trips-row--planned' : 'oal-trips-row'}>
            <td className="oal-trips-date">{fmtTripDates(t)}</td>
            <td className="oal-trips-city">{t.city ?? t.destination}</td>
            <td className="oal-trips-lodging">
              {t.lodging ?? ''}
              {t.planned && <span className="oal-trips-tag">planned</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

